import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Breadcrumbs, Button, type PageAction, type PageTitleBadge, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { isValidationError, useAggregateAPIOperationStatus, useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { CompliancePolicyVersionResource, ModifyPolicyCommand, PlatformHubVersionControlSettingsResource, PolicyBffResource, PublishCompliancePolicyCommand, Repository, UpdatePolicyActivationStatusCommand, ValidateGitRefV2Response, } from "@octopusdeploy/octopus-server-client";
import { OctopusError, Permission, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { PlatformHubVcsError } from "~/areas/platformHub/PlatformHubVcsError";
import type { PolicyActivationStatus } from "~/areas/platformHub/policies/PolicyActivationStatusDialog";
import { PolicySettingsTab } from "~/areas/platformHub/policies/PolicySettingsTab";
import { PolicyVersionsTab } from "~/areas/platformHub/policies/PolicyVersionsTab";
import type { PolicyWizardSaveParams } from "~/areas/platformHub/policies/PolicyWizardDialog";
import { PolicyActionCompleted, PolicyWizardDialog, PolicyWizardSteps } from "~/areas/platformHub/policies/PolicyWizardDialog";
import { policyStarters } from "~/areas/platformHub/policies/policyStarters";
import { useRequiredPlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import type { FieldErrors } from "~/components/DataBaseComponent/Errors";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import type { PageContentCalloutType } from "~/components/PageContent/PageContent";
import { PageContent } from "~/components/PageContent/PageContent";
import { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import { PermissionCheck } from "~/components/PermissionCheck/index";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
interface EditPolicyPageProps {
    loaderData: LoaderData;
    slug: string;
    queryParams: EditPolicyPageQueryParams;
    setQueryParams: QueryParamValuesSetter<EditPolicyPageQueryParams>;
}
type EditPoliciesActiveTab = "settings" | "versions";
export interface EditPolicyPageQueryParams {
    gitRef: string;
    activeTab: EditPoliciesActiveTab;
    autoPublish?: boolean;
}
type LoaderData = DataWithPolicy | DataWithFailedGitRefValidationResult;
type DataWithPolicy = {
    policy: PolicyBffResource;
    versions: CompliancePolicyVersionResource[];
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    gitRefValidationResult: ValidateGitRefV2Response | undefined;
};
type DataWithFailedGitRefValidationResult = {
    gitRefValidationResult: ValidateGitRefV2Response;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
};
interface QuickCommitParams {
    type: "Quick Commit";
}
export type SavePolicyParams = QuickCommitParams | PolicyWizardSaveParams;
export async function editPolicyPageLoader(repository: Repository, slug: string, queryParams: EditPolicyPageQueryParams): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    let gitRef = queryParams.gitRef;
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const gitRefValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    if (gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return {
            persistenceSettings,
            gitRefValidationResult,
        };
    }
    const [policyResponse, versions] = await Promise.all([repository.Policy.get(slug, gitRef), repository.Policy.getVersions(slug)]);
    return {
        policy: policyResponse.Policy,
        versions: versions,
        persistenceSettings,
        gitRefValidationResult,
    };
}
export function EditPolicyPage({ loaderData, slug, queryParams, setQueryParams }: EditPolicyPageProps) {
    const [gitRef, setGitRef, gitRefValidationResult] = useRequiredPlatformHubGitRef(loaderData.persistenceSettings, queryParams, setQueryParams, loaderData.gitRefValidationResult);
    const { status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress, errors } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    const [disableDirtyFormChecking, setDisableDirtyFormChecking] = useState<boolean>(false);
    const showSnackbar = useShowSnackbar();
    const [wizardSteps, setWizardSteps] = useState<PolicyWizardSteps[]>([]);
    const { openDialog: openWizardDialog, isOpen: isWizardDialogOpen, closeAllDialogs: closeCommitDialog } = useDialogTrigger();
    const [model, setModel] = useState<PolicyBffResource | null>(isDataWithPolicy(loaderData) ? loaderData.policy : null);
    const [publishedVersions, setPublishedVersions] = useState<CompliancePolicyVersionResource[]>(isDataWithPolicy(loaderData) ? loaderData.versions : []);
    const [error, setError] = useState<Error | undefined>();
    const [selectedPolicyVersion, setSelectedPolicyVersion] = useState<CompliancePolicyVersionResource | undefined>(undefined);
    const isStarterPoliciesActive = useOctopusFeatureToggle("starter-policies", false);
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const { result: cleanModel, refetch: reloadPolicy } = useQuery(async (repository) => {
        if (!gitRef) {
            return null;
        }
        if (gitRefValidationResult && gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
            return null;
        }
        const [policyResponse, versions] = await Promise.all([repository.Policy.get(slug, gitRef), repository.Policy.getVersions(slug)]);
        setModel(policyResponse.Policy);
        setPublishedVersions(versions);
        return policyResponse.Policy;
    }, [slug, gitRef, gitRefValidationResult], "Get policy", {
        initialResult: isDataWithPolicy(loaderData) ? loaderData.policy : null,
    });
    const matchedStarter = isStarterPoliciesActive ? findMatchedStarter(cleanModel) : undefined;
    const hasPlaceholders = matchedStarter !== undefined && model !== null && matchedStarter.Placeholders.some((p) => model.ConditionsRego.includes(p));
    useEffect(() => {
        if (isWizardDialogOpen)
            return;
        if (isInProgress)
            return;
        if (queryParams.autoPublish && isEqual(model, cleanModel) && hasPermission(Permission.PlatformHubEdit)) {
            setWizardSteps([PolicyWizardSteps.Publish, PolicyWizardSteps.Activate]);
            openWizardDialog();
            setQueryParams((prev) => ({ ...prev, autoPublish: undefined }));
        }
    }, [isWizardDialogOpen, isInProgress, queryParams, model, cleanModel, setQueryParams, openWizardDialog]);
    const savePolicyInternal = async (isNavigationConfirmation: boolean, commitMessage?: CommitMessageWithDetails, repository?: Repository) => {
        if (isNavigationConfirmation) {
            setWizardSteps([PolicyWizardSteps.Commit, PolicyWizardSteps.Publish, PolicyWizardSteps.Activate]);
            openWizardDialog();
        }
        else if (repository) {
            if (model) {
                const updatedPolicy: ModifyPolicyCommand = {
                    ViolationAction: model.ViolationAction,
                    Description: model.Description,
                    ViolationReason: model.ViolationReason,
                    Slug: model.Slug,
                    ScopeRego: model.ScopeRego,
                    ConditionsRego: model.ConditionsRego,
                    GitRef: gitRef,
                    Name: model.Name,
                    ChangeDescription: getFormattedCommitMessage(commitMessage ?? { summary: "", details: "" }, getDefaultCommitMessage()),
                };
                await repository.Policy.modify(updatedPolicy);
            }
        }
    };
    const publishPolicy = async (repository: Repository, version: string) => {
        if (!model) {
            return;
        }
        const publishVersion: PublishCompliancePolicyCommand = {
            Slug: model.Slug,
            GitRef: model.GitRef,
            Version: version,
        };
        return await repository.Policy.publish(publishVersion);
    };
    const savePolicyActivationStatus = async (repository: Repository, version: string, status: PolicyActivationStatus) => {
        if (!model) {
            return;
        }
        const updateStatus: UpdatePolicyActivationStatusCommand = {
            Slug: model.Slug,
            Version: version,
            IsActive: status === "Active",
        };
        return await repository.Policy.updateActivationStatus(updateStatus);
    };
    const { execute: savePolicy } = useMutation({
        name: "Save policy",
        action: async (repository: Repository, params: SavePolicyParams) => {
            if (params.type === "Quick Commit") {
                await savePolicyInternal(false, undefined, repository);
                return [PolicyActionCompleted.Committed];
            }
            else {
                const { commitParams, publishParams, activationStatusParams } = params;
                const actionsCompleted: PolicyActionCompleted[] = [];
                if (commitParams) {
                    await savePolicyInternal(false, commitParams.commitMessage, repository);
                    actionsCompleted.push(PolicyActionCompleted.Committed);
                }
                if (publishParams) {
                    await publishPolicy(repository, publishParams.versionToPublish);
                    actionsCompleted.push(PolicyActionCompleted.Published);
                }
                if (activationStatusParams) {
                    await savePolicyActivationStatus(repository, activationStatusParams.version, activationStatusParams.status);
                    const completed = activationStatusParams.status === "Active" ? PolicyActionCompleted.Activated : PolicyActionCompleted.Deactivated;
                    actionsCompleted.push(completed);
                }
                return actionsCompleted;
            }
        },
        onError: (error) => {
            setError(error);
        },
        afterComplete: async (repository, result) => {
            const snackbarMessage = createSnackbarMessage(result);
            if (snackbarMessage) {
                showSnackbar(snackbarMessage);
            }
            reloadPolicy();
            setError(undefined);
            setSelectedPolicyVersion(undefined);
        },
    });
    function createSnackbarMessage(actionsCompleted: PolicyActionCompleted[]): string {
        if (actionsCompleted.length === 0) {
            return "";
        }
        if (actionsCompleted.length === 1) {
            return `Successfully ${actionsCompleted[0]} the policy.`;
        }
        const allButLast = actionsCompleted.slice(0, -1).join(", ");
        const last = actionsCompleted[actionsCompleted.length - 1];
        return `Successfully ${allButLast} and ${last} the policy.`;
    }
    const commitButton: PageAction = {
        key: "CommitButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <div role="group" className={styles.commitButtonContainerStyles}>
                    <ActionButton label={"Commit"} type={ActionButtonType.Primary} accessibleName={"Commit"} onClick={() => {
                if (hasPlaceholders) {
                    setHasAttemptedSave(true);
                    return;
                }
                setWizardSteps([PolicyWizardSteps.Commit, PolicyWizardSteps.Publish, PolicyWizardSteps.Activate]);
                openWizardDialog();
            }} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || isInProgress || !hasPermission(Permission.PlatformHubEdit)}/>
                    <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={"Quick commit"} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || isInProgress || !hasPermission(Permission.PlatformHubEdit)} onClick={() => {
                if (hasPlaceholders) {
                    setHasAttemptedSave(true);
                    return;
                }
                savePolicy({ type: "Quick Commit" });
            }}/>
                </div>
            </PermissionCheck>),
    };
    const publishButton: PageAction = {
        key: "PublishButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ActionButton label={"Publish"} type={ActionButtonType.Secondary} accessibleName={"Publish"} onClick={() => {
                if (hasPlaceholders) {
                    setHasAttemptedSave(true);
                    return;
                }
                setWizardSteps([PolicyWizardSteps.Publish, PolicyWizardSteps.Activate]);
                openWizardDialog();
            }} disabled={!isEqual(model, cleanModel) || isInProgress}/>
            </PermissionCheck>),
    };
    const secondaryPageActions = [publishButton];
    const openActivationStatusDialog = (version: CompliancePolicyVersionResource) => {
        setSelectedPolicyVersion(version);
        setWizardSteps([PolicyWizardSteps.Activate]);
        openWizardDialog();
    };
    function getDefaultCommitMessage(): string {
        return "Update policy";
    }
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings}/>;
    const setActiveTab = useCallback((value: EditPoliciesActiveTab) => setQueryParams((prev) => ({ ...prev, activeTab: value })), [setQueryParams]);
    const fieldErrors = useMemo(() => getFieldErrors(error ?? null), [error]);
    function getFieldErrors(possibleError: Error | null): FieldErrors {
        if (possibleError === null) {
            return {};
        }
        if (possibleError instanceof OctopusError) {
            return possibleError.Details ?? {};
        }
        if (isValidationError(possibleError)) {
            return possibleError.fieldErrors;
        }
        return {};
    }
    const getFieldError = useCallback((fieldName: string) => {
        if (Object.prototype.hasOwnProperty.call(fieldErrors, fieldName)) {
            return fieldErrors[fieldName];
        }
        const found = Object.keys(fieldErrors).find((k) => k.toLowerCase() === fieldName.toLowerCase());
        if (found) {
            return fieldErrors[found];
        }
        const foundPartialMatch = Object.keys(fieldErrors).find((k) => k.endsWith("." + fieldName));
        if (foundPartialMatch) {
            return fieldErrors[foundPartialMatch];
        }
    }, [fieldErrors]);
    function determineCallout(): PageContentCalloutType | undefined {
        if (!model || error) {
            return undefined;
        }
        if (model.Warning) {
            return {
                type: "warning",
                title: model.Warning.Message,
                content: model.Warning.Details,
            };
        }
    }
    if (isDataWithInvalidGitRef(loaderData, gitRefValidationResult)) {
        const persistenceSettings = loaderData.persistenceSettings;
        return (<PageContent header={{ title: "Edit Policy", contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/> }}>
                <PlatformHubVcsError error={gitRefValidationResult || loaderData.gitRefValidationResult} gitRef={gitRef} persistenceSettings={persistenceSettings} setGitRef={setGitRef}/>
            </PageContent>);
    }
    if (!cleanModel || !model) {
        return (<PageContent header={{ title: "Edit Policy" }}>
                <div>Loading...</div>
            </PageContent>);
    }
    function getSettingsTabWarning() {
        if (model?.Warning) {
            return model.Warning.Message;
        }
        if (error) {
            return "An error occurred while saving. Please review the errors and try again.";
        }
        return hasPlaceholders && hasAttemptedSave ? "Please provide all required details and try again." : undefined;
    }
    return (<>
            <header className={styles.pageHeaderContainer}>
                <div className={styles.breadcrumbContainer}>
                    <Breadcrumbs items={[{ label: "Policies", pageUrl: links.policiesPage.generateUrl() }]} showBackIcon={true}/>
                </div>
                <div className={styles.titleContainer}>
                    <h1 title={model.Name ?? "Edit Policy"} className={styles.title}>
                        {model.Name ?? "Edit Policy"}
                    </h1>
                </div>
            </header>
            <LegacyForm devToolsDirtyTrackingKey="Policy" disableDirtyFormChecking={disableDirtyFormChecking} onSaveClick={savePolicyInternal} savePermission={{ permission: Permission.PlatformHubEdit }} model={model} cleanModel={cleanModel} forceDisableFormSaveButton={isInProgress}>
                {({ FormContent }) => (<PageContent header={{ title: "Details", contextSelector: titleAccessory, badge: getPolicyActivationBadge(publishedVersions), primaryAction: commitButton, pageActions: secondaryPageActions }} currentTab={queryParams.activeTab} onTabChanged={setActiveTab} callout={determineCallout()} tabs={[
                {
                    label: "Settings",
                    value: "settings",
                    warning: getSettingsTabWarning(),
                    content: (<FormContent hideExpandAll={false}>
                                        <PolicySettingsTab model={model} onChange={setModel} getFieldError={getFieldError} showPlaceholderAsError={hasAttemptedSave} isStarterPoliciesActive={isStarterPoliciesActive} placeholders={matchedStarter?.Placeholders}/>
                                    </FormContent>),
                },
                {
                    label: "Versions",
                    value: "versions",
                    content: <PolicyVersionsTab versions={publishedVersions} openActivationStatusDialog={openActivationStatusDialog}/>,
                },
            ]}></PageContent>)}
            </LegacyForm>

            <PolicyWizardDialog open={isWizardDialogOpen} busy={isInProgress} errors={errors} gitRef={gitRef} stepsToShow={wizardSteps} getDefaultCommitMessage={getDefaultCommitMessage} savePolicy={savePolicy} closeDialog={closeCommitDialog} key={isWizardDialogOpen ? "dialog-open" : "dialog-closed"} // This ensures the state is always new when the dialog is opened
     model={model} publishedVersions={publishedVersions} policyVersion={selectedPolicyVersion}/>
        </>);
}
function findMatchedStarter(model: PolicyBffResource | null) {
    if (!model)
        return undefined;
    return policyStarters.find((s) => s.ScopeRego.replace(/\$\{slug\}/g, model.Slug) === model.ScopeRego && s.ConditionsRego.replace(/\$\{slug\}/g, model.Slug) === model.ConditionsRego);
}
function isDataWithPolicy(data: LoaderData | DataWithFailedGitRefValidationResult): data is DataWithPolicy {
    return "policy" in data;
}
function isDataWithInvalidGitRef(data: LoaderData | DataWithFailedGitRefValidationResult, gitRefValidationResult: ValidateGitRefV2Response | undefined): data is DataWithFailedGitRefValidationResult {
    return gitRefValidationResult !== undefined && gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success;
}
function getPolicyActivationBadge(versions: CompliancePolicyVersionResource[]): PageTitleBadge {
    if (versions.length === 0) {
        return { variant: "neutral", label: "Unpublished" };
    }
    const activated = versions.find((v) => v.IsActive);
    if (activated) {
        return { variant: "info", label: activated.Version };
    }
    return { variant: "neutral", label: "Inactive" };
}
const styles = {
    pageHeaderContainer: css({
        display: "flex",
        flexDirection: "column",
        paddingBottom: space[32],
        gap: "unset",
    }),
    breadcrumbContainer: css({
        display: "flex",
        alignItems: "flex-start",
        height: "1.75rem",
    }),
    titleContainer: css({
        flex: "1",
        display: "flex",
        gap: space[12],
        alignItems: "center",
        overflow: "hidden",
    }),
    title: css({
        all: "unset",
        font: text.heading.large,
        color: themeTokens.color.text.primary,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    }),
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
};
export default EditPolicyPage;
